import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import logoUrbnet from '@/assets/images/UrbNet2LOGO.png';

gsap.registerPlugin(ScrollTrigger);

const TermosUsoPage = () => {
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(pageRef.current?.children || [],
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power3.out'
        }
      );
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Background gradient */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/8 via-background to-primary/5" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--secondary)/0.14),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,hsl(var(--primary)/0.10),transparent_50%)]" />
      </div>

      {/* Subtle dot grid */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(hsl(var(--foreground)) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div ref={pageRef} className="relative z-10 max-w-4xl mx-auto px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <img 
            src={logoUrbnet} 
            alt="UrbNet Logo" 
            className="h-20 mx-auto mb-6"
          />
          <h1 className="text-4xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-primary via-secondary to-tertiary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
            UrbNet Gestão
          </h1>
          <h2 className="text-2xl md:text-3xl font-semibold mb-4 text-foreground" style={{ fontFamily: 'Urbanist, sans-serif' }}>
            Módulo de Gestão de Ouvidorias
          </h2>
          <h3 className="text-xl md:text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
            TERMOS DE USO — GESTORES E AGENTES DE OUVIDORIA
          </h3>
          <p className="text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
            Última atualização: Junho de 2026
          </p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              Preâmbulo
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Ao acessar o módulo UrbNet Gestão — seja por meio do painel web, da interface de gestão ou da API institucional — o Gestor, Agente ou representante legal do órgão público declara ter lido, compreendido e concordado integralmente com os presentes Termos de Uso.
            </p>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Estes Termos regulam especificamente o acesso e uso do módulo de gestão de ouvidorias do UrbNet, destinado exclusivamente a prefeituras, secretarias, órgãos públicos, autarquias e entidades de interesse coletivo formalmente verificadas. São complementares aos Termos de Uso gerais da plataforma UrbNet, disponíveis no aplicativo.
            </p>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              Caso não concorde com qualquer disposição deste instrumento, o órgão deve se abster de utilizar o módulo UrbNet Gestão e comunicar o UrbNet para encerramento do acesso.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              1. Sobre o Módulo UrbNet Gestão
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Marelli Desenvolvimentos Eireli, inscrita no CNPJ/MF sob o nº 35.856.740/0001-09, com sede em Cachoeiro de Itapemirim, Espírito Santo, doravante denominada URBNET, disponibiliza o módulo UrbNet Gestão como ambiente exclusivo de gestão institucional da plataforma UrbNet.
            </p>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet Gestão permite que órgãos públicos contratantes:
            </p>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li>Recebam, visualizem, classifiquem e respondam às solicitações dos cidadãos encaminhadas pela plataforma UrbNet;</li>
              <li>Gerenciem o cadastro, verificação e hierarquia de ouvidorias setoriais vinculadas ao município;</li>
              <li>Cadastrem, aprovem, reprovem e monitorem Agentes de Ouvidoria autorizados a operar em nome do órgão;</li>
              <li>Acompanhem métricas de desempenho, volumes de solicitações, prazos de atendimento e indicadores de satisfação cidadã;</li>
              <li>Acessem relatórios institucionais para prestação de contas e auditorias dos Tribunais de Contas.</li>
            </ul>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet Gestão não é sistema de gestão interna de processos administrativos, protocolo eletrônico, SEI, ou substituto de sistemas oficiais de ouvidoria do governo federal. Seu escopo é restrito à gestão das demandas recebidas pela plataforma UrbNet.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              2. Definições
            </h2>
            <ul className="space-y-3 text-muted-foreground list-disc list-inside" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li><strong>Gestor:</strong> servidor público ou representante legal do órgão contratante com acesso administrativo pleno ao painel UrbNet Gestão do município, responsável pela configuração, aprovação de agentes e supervisão geral das ouvidorias;</li>
              <li><strong>Agente:</strong> servidor ou colaborador autorizado pelo Gestor a operar dentro de uma ouvidoria específica — recebendo, respondendo e encaminhando solicitações de cidadãos;</li>
              <li><strong>Prefeitura Supervisora:</strong> prefeitura municipal responsável pelo contrato com o UrbNet e pela validação das ouvidorias setoriais do município. Detém acesso de nível superior às ouvidorias vinculadas;</li>
              <li><strong>Ouvidoria Setorial:</strong> órgão ou secretaria municipal registrada no UrbNet Gestão pela Prefeitura Supervisora, com escopo temático definido (ex.: Secretaria de Obras, SAAE, Secretaria de Saúde);</li>
              <li><strong>Solicitação:</strong> registro de problema urbano enviado por cidadão via app UrbNet, contendo descrição, categoria, geolocalização e evidência fotográfica validada por IA;</li>
              <li><strong>Plano de Assinatura:</strong> contrato firmado entre o UrbNet e o órgão contratante, definindo volume de validações por IA incluídas, recursos disponíveis e condições de SLA;</li>
              <li><strong>Validação por IA:</strong> processo automatizado de triagem de evidências fotográficas das solicitações, realizado pelo Google Gemini, antes do encaminhamento às ouvidorias.</li>
            </ul>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              3. Acesso e Credenciais
            </h2>
            <div className="space-y-4 text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div>
                <h3 className="font-semibold text-foreground mb-2">3.1 Credenciais institucionais</h3>
                <p className="leading-relaxed mb-2">O acesso ao UrbNet Gestão é concedido exclusivamente mediante contrato vigente com o UrbNet. As credenciais de acesso são nominais, intransferíveis e vinculadas ao CPF do servidor cadastrado.</p>
                <ul className="space-y-1 list-disc list-inside mb-2">
                  <li>O Gestor é o único responsável pela criação e revogação de credenciais de Agentes no painel;</li>
                  <li>Credenciais não devem ser compartilhadas entre servidores — cada operador deve ter seu próprio acesso;</li>
                  <li>O órgão deve comunicar o UrbNet imediatamente em caso de desligamento de servidor com acesso ativo, para revogação das credenciais;</li>
                  <li>O UrbNet não se responsabiliza por ações realizadas com credenciais não revogadas após o desligamento do servidor.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">3.2 Níveis de acesso</h3>
                <ul className="space-y-1 list-disc list-inside">
                  <li><strong>Gestor Municipal (Prefeitura Supervisora):</strong> acesso pleno — cadastra ouvidorias setoriais, aprova agentes, configura prazos, acessa todos os relatórios e métricas do município;</li>
                  <li><strong>Gestor de Ouvidoria Setorial:</strong> acesso restrito à sua ouvidoria — gerencia agentes vinculados, responde solicitações, acessa relatórios da sua secretaria;</li>
                  <li><strong>Agente:</strong> acesso operacional — visualiza e responde solicitações atribuídas, registra encaminhamentos e atualizações de status.</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              4. Gestão de Ouvidorias
            </h2>
            <div className="space-y-4 text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div>
                <h3 className="font-semibold text-foreground mb-2">4.1 Cadastro e hierarquia</h3>
                <p className="leading-relaxed mb-2">O cadastro de ouvidorias no UrbNet Gestão segue o sistema de três níveis da plataforma. Apenas a Prefeitura Supervisora pode cadastrar ouvidorias setoriais dentro de seu município. Cidadãos não têm acesso direto ao cadastro de ouvidorias no módulo Gestão.</p>
                <p className="leading-relaxed mb-2">Ao cadastrar uma ouvidoria setorial, o Gestor Municipal declara:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Que o órgão cadastrado existe formalmente e possui vínculo institucional com o município;</li>
                  <li>Que os dados informados — nome, CNPJ ou código de identificação, responsável e contato — são verídicos e atualizados;</li>
                  <li>Que a ouvidoria cadastrada tem competência legal para receber e tratar as categorias de solicitação a ela atribuídas.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">4.2 Responsabilidade sobre ouvidorias cadastradas</h3>
                <p className="leading-relaxed">O órgão contratante é integralmente responsável pela veracidade dos dados das ouvidorias que cadastrar. O UrbNet não verifica de forma independente a legitimidade de cada ouvidoria setorial cadastrada pelo Gestor Municipal — essa responsabilidade recai sobre a Prefeitura Supervisora.</p>
                <p className="leading-relaxed">O cadastro irregular ou fraudulento de ouvidorias pode resultar em suspensão imediata do acesso ao módulo Gestão e comunicação às autoridades competentes, incluindo o Tribunal de Contas do estado.</p>
              </div>
            </div>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              5. Gestão de Agentes
            </h2>
            <div className="space-y-4 text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div>
                <h3 className="font-semibold text-foreground mb-2">5.1 Processo de aprovação</h3>
                <p className="leading-relaxed mb-2">Todo Agente de Ouvidoria precisa passar pelo processo de aprovação antes de ter acesso ao painel operacional. O fluxo é:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>O candidato a Agente realiza cadastro no app UrbNet e solicita vinculação à ouvidoria;</li>
                  <li>O Gestor da ouvidoria recebe a solicitação e analisa os dados do candidato;</li>
                  <li>O Gestor aprova ou reprova o candidato diretamente pelo painel UrbNet Gestão;</li>
                  <li>O candidato recebe notificação automática com o resultado e, se aprovado, obtém acesso ao painel operacional.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">5.2 Responsabilidade sobre agentes aprovados</h3>
                <p className="leading-relaxed mb-2">Ao aprovar um Agente, o Gestor declara que:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>O servidor é colaborador ou funcionário do órgão com atribuição compatível com a função de agente de ouvidoria;</li>
                  <li>O servidor foi devidamente orientado sobre as obrigações previstas nestes Termos e sobre as normas de sigilo e proteção de dados;</li>
                  <li>O órgão assume responsabilidade pelas ações do agente, realizadas dentro do painel UrbNet Gestão.</li>
                </ul>
                <p className="leading-relaxed">Agentes que realizarem ações em desconformidade com estes Termos devem ter o acesso imediatamente revogado pelo Gestor. O UrbNet pode revogar o acesso de qualquer Agente independentemente, se identificar uso indevido.</p>
              </div>
            </div>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              6. Atendimento às Solicitações
            </h2>
            <div className="space-y-4 text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div>
                <h3 className="font-semibold text-foreground mb-2">6.1 Obrigações de atendimento</h3>
                <p className="leading-relaxed mb-2">Ao contratar o UrbNet Gestão, o órgão se compromete a:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Designar ao menos um Agente ativo e responsável para cada ouvidoria verificada cadastrada;</li>
                  <li>Atualizar o status das solicitações conforme o andamento real do atendimento — não sendo permitido marcar solicitações como resolvidas sem a devida providência;</li>
                  <li>Tratar as solicitações dos cidadãos com respeito, objetividade e dentro das competências do órgão.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">6.2 Prazo vencido e escalonamento</h3>
                <p className="leading-relaxed">O UrbNet não se responsabiliza por impactos institucionais decorrentes da visibilidade pública de prazos vencidos — essa visibilidade é inerente ao modelo de transparência da plataforma e foi aceita no momento da contratação.</p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">6.3 Usos proibidos no atendimento</h3>
                <p className="leading-relaxed mb-2">É expressamente proibido ao Gestor e aos Agentes:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Excluir, ocultar ou alterar indevidamente solicitações de cidadãos para maquiar métricas de desempenho;</li>
                  <li>Marcar solicitações como resolvidas sem a devida providência operacional;</li>
                  <li>Usar as informações e dados dos cidadãos para finalidades distintas do atendimento da solicitação;</li>
                  <li>Compartilhar dados de solicitações ou identidades de cidadãos com terceiros não autorizados;</li>
                  <li>Utilizar o painel para monitoramento, perseguição ou qualquer forma de discriminação de cidadãos específicos.</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              7. Dados e Privacidade
            </h2>
            <div className="space-y-4 text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div>
                <h3 className="font-semibold text-foreground mb-2">7.1 Natureza dos dados acessados</h3>
                <p className="leading-relaxed">O acesso ao UrbNet Gestão implica o tratamento de dados pessoais de cidadãos — incluindo nome, localização geográfica, fotografia do problema relatado e, em alguns casos, CPF do solicitante. O órgão contratante é considerado Controlador dos dados dos cidadãos que interagem com suas ouvidorias, nos termos da Lei nº 13.709/2018 (LGPD).</p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">7.2 Obrigações do órgão contratante como Controlador</h3>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Tratar os dados dos cidadãos exclusivamente para fins de atendimento das solicitações recebidas;</li>
                  <li>Não compartilhar, exportar ou transferir dados de cidadãos a terceiros sem base legal adequada;</li>
                  <li>Garantir que todos os servidores com acesso ao painel foram orientados sobre sigilo e proteção de dados;</li>
                  <li>Atender às solicitações de titulares de dados — direito de acesso, correção, exclusão — no prazo legal de 15 dias úteis;</li>
                  <li>Comunicar o UrbNet em caso de incidente de segurança que envolva dados de cidadãos acessados pelo painel Gestão.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">7.3 Responsabilidade compartilhada</h3>
                <p className="leading-relaxed">O UrbNet atua como Operador dos dados no contexto do UrbNet Gestão, processando as informações conforme instruções do órgão Controlador. O UrbNet adota medidas técnicas e organizacionais adequadas para garantir a segurança dos dados, mas não se responsabiliza por incidentes decorrentes de uso indevido das credenciais ou de ações dos Agentes do órgão contratante.</p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">7.4 Uso de dados anonimizados</h3>
                <p className="leading-relaxed">O UrbNet poderá utilizar dados agregados e anonimizados gerados pela plataforma — sem identificação de cidadãos, servidores ou órgãos específicos — para fins de melhoria do serviço, publicações estatísticas, benchmarks nacionais de gestão urbana e desenvolvimento de relatórios setoriais.</p>
              </div>
            </div>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              8. Planos de Assinatura e Financeiro
            </h2>
            <div className="space-y-4 text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div>
                <h3 className="font-semibold text-foreground mb-2">8.1 Condições gerais</h3>
                <p className="leading-relaxed">O acesso ao UrbNet Gestão está condicionado à vigência de um Plano de Assinatura contratado. Os planos são definidos conforme o porte do município e incluem volume mensal de validações por IA, recursos disponíveis e nível de SLA de suporte técnico.</p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">8.2 Excedente de validações</h3>
                <p className="leading-relaxed mb-2">Cada plano inclui um volume mensal de validações por IA. Ao atingir o limite do plano, as validações excedentes serão cobradas por uso conforme tabela vigente. O Gestor receberá alerta automático ao atingir 80% do volume contratado.</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>O órgão poderá optar por bloquear novas validações ao atingir o limite, ou autorizar o consumo excedente;</li>
                  <li>A opção de bloqueio deve ser configurada pelo Gestor no painel — o padrão é autorizar excedente com cobrança posterior;</li>
                  <li>Faturas de excedente serão emitidas no ciclo seguinte ao consumo.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">8.3 Cancelamento e suspensão</h3>
                <ul className="space-y-1 list-disc list-inside">
                  <li>O cancelamento do contrato deve ser comunicado com antecedência mínima de 30 (trinta) dias;</li>
                  <li>O não pagamento por mais de 15 (quinze) dias resultará em suspensão automática do acesso ao painel;</li>
                  <li>A suspensão não exclui os dados do órgão — eles ficam retidos por 90 (noventa) dias após o cancelamento, período em que o órgão pode solicitar exportação;</li>
                  <li>Após 90 dias do cancelamento, os dados são permanentemente excluídos dos servidores do UrbNet.</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              9. Auditoria e Transparência
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet Gestão gera logs de todas as ações realizadas no painel — incluindo alterações de status, respostas enviadas, aprovações de agentes e modificações de configuração. Esses logs:
            </p>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li>São armazenados pelo UrbNet por período mínimo de 5 (cinco) anos;</li>
              <li>Podem ser fornecidos ao Tribunal de Contas estadual ou federal mediante requisição legal ou judicial;</li>
              <li>Podem ser acessados pelo órgão contratante para fins de auditoria interna;</li>
              <li>Não podem ser alterados ou excluídos por Gestores ou Agentes.</li>
            </ul>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              O órgão contratante declara ciência de que todas as ações realizadas no painel são rastreáveis e auditáveis, e que essa rastreabilidade é elemento essencial do modelo de transparência pública da plataforma.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              10. Suspensão e Encerramento de Acesso
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet poderá suspender ou encerrar o acesso ao módulo Gestão, a qualquer tempo, nos seguintes casos:
            </p>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li>Inadimplência superior a 15 (quinze) dias;</li>
              <li>Violação de qualquer cláusula destes Termos ou dos Termos de Uso gerais da plataforma;</li>
              <li>Uso do painel para finalidades ilegais, antiéticas ou que comprometam a integridade da plataforma;</li>
              <li>Determinação judicial, administrativa ou de Tribunal de Contas;</li>
              <li>Identificação de risco grave à segurança de dados de cidadãos.</li>
            </ul>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              Em caso de suspensão por violação, o órgão será notificado com antecedência de 48 (quarenta e oito) horas — exceto em casos de urgência ou risco imediato, onde a suspensão pode ser imediata com notificação posterior.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              11. Suporte Técnico
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet oferece suporte técnico ao módulo Gestão conforme nível do plano contratado:
            </p>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li><strong>Plano Essencial e Profissional:</strong> suporte por e-mail e chat, tempo de resposta em até 48 horas úteis;</li>
              <li><strong>Plano Avançado e Institucional:</strong> suporte prioritário, tempo de resposta em até 24 horas úteis;</li>
              <li><strong>Plano Enterprise Gestão:</strong> gerente de conta dedicado, SLA de resposta em até 4 horas úteis e suporte por telefone.</li>
            </ul>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              Incidentes críticos — indisponibilidade total do painel ou perda de dados — têm SLA de resposta imediata independente do plano.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              12. Propriedade Intelectual
            </h2>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              Todos os direitos de propriedade intelectual relativos ao módulo UrbNet Gestão — código-fonte, algoritmos, interfaces, relatórios gerados e metodologias — são de titularidade exclusiva da Marelli Desenvolvimentos Eireli. O órgão contratante não adquire qualquer direito sobre o software, sendo o contrato de assinatura uma licença de uso temporária e não exclusiva.
            </p>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              Os dados das solicitações dos cidadãos pertencem aos próprios cidadãos, sendo o órgão Controlador e o UrbNet Operador, conforme LGPD.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              13. Modificações dos Termos
            </h2>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet reserva-se o direito de alterar estes Termos a qualquer momento. Alterações serão comunicadas ao Gestor cadastrado com antecedência mínima de 30 (trinta) dias por e-mail institucional e notificação no painel. O uso continuado do módulo Gestão após a entrada em vigor das alterações implica aceitação.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              14. Disposições Gerais
            </h2>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li>A tolerância do UrbNet quanto ao eventual descumprimento de qualquer cláusula não constitui novação, renúncia ou precedente;</li>
              <li>Caso qualquer disposição seja considerada inválida, as demais permanecem em pleno vigor;</li>
              <li>O presente instrumento prevalece sobre entendimentos anteriores entre as partes;</li>
              <li>Estes Termos vigorarão enquanto o contrato de assinatura estiver ativo.</li>
            </ul>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              15. Legislação Aplicável e Foro
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Este instrumento é regido pelas leis da República Federativa do Brasil, em especial pela Lei nº 13.709/2018 (LGPD), Lei nº 12.965/2014 (Marco Civil da Internet), Lei nº 14.133/2021 (Lei de Licitações e Contratos Administrativos) e Lei nº 8.078/1990 (Código de Defesa do Consumidor).
            </p>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              Fica eleito o foro da Comarca de Cachoeiro de Itapemirim, Estado do Espírito Santo, para dirimir quaisquer controvérsias, com renúncia expressa a qualquer outro, por mais privilegiado que seja.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              16. Contato Institucional
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Para dúvidas, notificações, solicitações de privacidade ou comunicação de incidentes:
            </p>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li>E-mail institucional: gestao@gestao.urbnet.app.br</li>
              <li>Canal de suporte no painel: Menu &gt;</li>
              <li>Endereço da sede: Cachoeiro de Itapemirim, Espírito Santo, Brasil</li>
            </ul>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40 text-center">
            <p className="font-semibold text-foreground mb-2" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              Marelli Desenvolvimentos Eireli
            </p>
            <p className="text-muted-foreground mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              CNPJ: 35.856.740/0001-09
            </p>
            <p className="text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              Cachoeiro de Itapemirim — ES — Brasil
            </p>
            <p className="text-muted-foreground mt-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              Última atualização: Junho de 2026
            </p>
          </section>
        </div>

        {/* Back button */}
        <div className="mt-12 text-center">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-6 py-3 text-lg font-semibold text-white bg-gradient-to-r from-primary to-secondary rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Voltar à Página Inicial
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermosUsoPage;
